import React, {useState, useEffect, useRef} from 'react';
import {
    Megaphone, Plus, Send, Users, CheckCircle, XCircle, Clock,
    Search, Eye, Trash2, MessageSquare, Phone, RefreshCw,
    X, Loader, Check,
    AlertTriangle, Calendar
} from 'lucide-react';
import {useInfiniteQuery} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {api} from '../../config/api';
import secureStorage from '../../utils/secureStorage';
import axios from 'axios';
import ActionMenu from '../Common/ActionMenu';
import Button from '../Common/Button';
import {
    HeaderAction, SearchInput, ToolbarSelect, ActiveFilters, SummaryStrip, ListCard, ListEmpty,
    thClass, mobileListClass, mobileRowClass, Avatar, Modal, ModalFooter
} from '../../stock/StockUI';
import {inputClass} from '../../ClientsTable/clientFormUI';

const fieldClass = inputClass(false);
const labelClass = 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block';
// Small uppercase caption, same as the SummaryStrip labels
const captionClass = 'text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const sectionClass = 'px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700';

// Full-width « Fermer » footer row
const CloseFooter = ({onClose}) => (
    <button type="button" onClick={onClose}
            className="w-full py-4 border-t border-gray-200 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
        Fermer
    </button>
);

// Label / value line of a hairline list
const DetailRow = ({label, value}) => (
    <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{value}</span>
    </div>
);

const ChannelBadge = ({channel}) => (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
        channel === 'whatsapp'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    }`}>
        {channel === 'whatsapp' ? <MessageSquare size={12}/> : <Phone size={12}/>}
        {channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}
    </span>
);

const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('fr-FR', {day: '2-digit', month: 'short', year: 'numeric'})
    : '—';

// Campaign row actions: one "⋯" menu for desktop rows and mobile cards
const CampaignActions = ({campaign, onView, onDelete}) => (
    <ActionMenu
        label={`Actions pour ${campaign.name || 'la campagne'}`}
        items={[
            {label: 'Voir les détails', icon: Eye, onClick: () => onView(campaign)},
            {divider: true},
            {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(campaign.id)},
        ]}
    />
);

const WHATSAPP_API = 'https://ws-send.quickcom.ma';

// ─── Fetch helpers ───────────────────────────────────────────────────────────

const getHeaders = async () => {
    const account = await secureStorage.getItem('selectedAccount');
    return {'X-account-id': account?.id};
};

// ─── Client field helpers ─────────────────────────────────────────────────────

/**
 * Build a display name from firstName + lastName.
 * Guard against lastName being stored as a phone number (digits-only).
 */
const getClientName = (c) => {
    const first = (c.firstName || '').trim();
    const last = (c.lastName || '').trim();
    // If lastName looks like a phone number, ignore it
    const lastIsPhone = /^[+\d\s()-]{4,}$/.test(last) && /\d{4}/.test(last);
    return [first, lastIsPhone ? '' : last].filter(Boolean).join(' ') || 'Sans nom';
};

/**
 * Return a normalised E.164-style phone string for WhatsApp sending.
 * Combines countryCode + phone, strips duplicates.
 */
const getClientPhone = (c) => {
    let phone = (c.phone || '').trim();
    const cc = (c.countryCode || '').replace(/^\+/, '').trim(); // e.g. "212"

    if (!phone) return '';

    // Already full international format (starts with +)
    if (phone.startsWith('+')) return phone.replace(/\s+/g, '');

    // If phone already starts with the country code digits, don't prepend again
    if (cc && !phone.startsWith(cc)) {
        // Remove leading 0 for local format (e.g. 0670143911 → 670143911)
        const local = phone.replace(/^0+/, '');
        return cc + local;
    }

    return phone.replace(/\s+/g, '');
};

/** E.164 lite: stripped number must be 6–15 digits */
const validatePhone = (phone) => {
    if (!phone) return false;
    return /^\d{6,15}$/.test(phone.replace(/[\s+\-().]/g, ''));
};

/** Remove clients sharing the same normalised phone, keep first occurrence */
const deduplicateByPhone = (clients) => {
    const seen = new Set();
    return clients.filter(c => {
        const key = (c.phone || '').replace(/[\s+]/g, '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const PAGE_SIZE = 30;

const fetchClientsPage = async ({pageParam, queryKey}) => {
    const [, search] = queryKey;
    const headers = await getHeaders();
    const params = new URLSearchParams({
        itemsPerPage: PAGE_SIZE,
        page: pageParam,
    });
    if (search) params.set('search', search);
    const {data} = await api.get(`/clients?${params.toString()}`, {headers});
    const raw = data?.data || (Array.isArray(data) ? data : []);
    // Support pagination shapes: {pagination:{currentPage,totalPages}} | {last_page} | {meta:{last_page}}
    const totalPages =
        data?.pagination?.totalPages ||
        data?.last_page ||
        data?.meta?.last_page ||
        (raw.length < PAGE_SIZE ? pageParam : pageParam + 1);
    const hasMore = pageParam < totalPages;
    return {
        items: raw.map(c => ({
            ...c,
            name: getClientName(c),
            phone: getClientPhone(c),
        })),
        nextPage: hasMore ? pageParam + 1 : undefined,
        totalPages,
    };
};

const fetchWhatsAppStatus = async (accountId) => {
    try {
        const {data} = await axios.get(`${WHATSAPP_API}/generate-qr/${accountId}`);
        return data;
    } catch {
        return {status: false, message: 'Hors ligne'};
    }
};

// ─── Campaign storage (localStorage) ────────────────────────────────────────

const STORAGE_KEY = 'marketing_campaigns';

const loadCampaigns = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
        return [];
    }
};

const saveCampaigns = (campaigns) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns));
};

// ─── Status Badge ────────────────────────────────────────────────────────────

const StatusBadge = ({status}) => {
    const map = {
        sent: {
            label: 'Envoyé',
            cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            icon: <CheckCircle size={12}/>
        },
        failed: {
            label: 'Échoué',
            cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
            icon: <XCircle size={12}/>
        },
        sending: {
            label: 'En cours',
            cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            icon: <Loader size={12} className="animate-spin"/>
        },
        draft: {
            label: 'Brouillon',
            cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
            icon: <Clock size={12}/>
        },
    };
    const s = map[status] || map.draft;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
            {s.icon} {s.label}
        </span>
    );
};

// ─── Test Send Modal ──────────────────────────────────────────────────────────

const TestSendModal = ({message, channel, accountId, onClose}) => {
    const [phones, setPhones] = useState('');
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState(null);

    const handleSend = async () => {
        const list = phones.split(/[,;\n]/).map(p => p.trim()).filter(Boolean);
        if (!list.length) { toast.error('Entrez au moins un numéro'); return; }
        const invalid = list.filter(p => !validatePhone(p.replace(/^\+/, '')));
        if (invalid.length) { toast.error(`Numéros invalides : ${invalid.join(', ')}`); return; }

        setSending(true);
        const log = [];
        for (const phone of list) {
            const cleaned = phone.replace(/[\s+]/g, '').replace(/^0+/, '');
            try {
                if (channel === 'whatsapp') {
                    await axios.post(`${WHATSAPP_API}/send`, {
                        number: accountId, message: `[TEST] ${message}`, to: cleaned, type: 'chat',
                    });
                }
                log.push({phone, ok: true});
            } catch (err) {
                log.push({phone, ok: false, error: err?.response?.data?.message || 'Erreur réseau'});
            }
        }
        setResults(log);
        setSending(false);
    };

    return (
        <Modal
            title="Envoi de test"
            subtitle="Messages préfixés [TEST] — non comptabilisés"
            onClose={onClose}
            size="sm"
            footer={results ? <CloseFooter onClose={onClose}/> : (
                <ModalFooter onCancel={onClose} onConfirm={handleSend} saving={sending}
                             disabled={!phones.trim()} label={sending ? 'Envoi…' : 'Envoyer le test'}/>
            )}
        >
            {!results ? (
                <div className="space-y-4">
                    <div>
                        <label htmlFor="test-phones" className={labelClass}>
                            Numéros <span className="text-gray-400 font-normal text-xs">(séparés par virgule)</span> :
                        </label>
                        <textarea
                            id="test-phones"
                            value={phones} onChange={e => setPhones(e.target.value)} rows={3}
                            placeholder="212612345678, 212698765432"
                            className={`${fieldClass} font-mono resize-none`}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Format sans + (ex : 212612345678)</p>
                    </div>
                    <div>
                        <p className={`${captionClass} mb-1`}>Aperçu</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 italic border-l-2 border-primary/40 pl-3">
                            [TEST] {message || '(aucun message)'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {results.filter(r => r.ok).length}/{results.length} envoyés
                    </p>
                    <div className="max-h-52 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                        {results.map((r, i) => (
                            <div key={i} className="flex items-center gap-3 py-2.5">
                                {r.ok ? <CheckCircle size={14} className="text-green-500 flex-shrink-0"/> : <XCircle size={14} className="text-red-500 flex-shrink-0"/>}
                                <div className="min-w-0">
                                    <p className="text-sm font-mono text-gray-700 dark:text-gray-300">{r.phone}</p>
                                    {r.error && <p className="text-xs text-red-500 truncate">{r.error}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ─── Campaign Details Modal ───────────────────────────────────────────────────

const CampaignDetailsModal = ({campaign, onClose}) => {
    if (!campaign) return null;
    return (
        <Modal title={campaign.name} subtitle={`Créée le ${formatDate(campaign.createdAt)}`} onClose={onClose}
               size="lg" bodyClass="" footer={<CloseFooter onClose={onClose}/>}>
            <SummaryStrip items={[
                {key: 'sent', label: 'Envoyés', value: campaign.stats?.sent || 0, color: 'text-green-600 dark:text-green-400'},
                {key: 'failed', label: 'Échoués', value: campaign.stats?.failed || 0, color: 'text-red-600 dark:text-red-400'},
                {key: 'invalid', label: 'Invalides', value: campaign.stats?.invalid || 0, color: 'text-amber-600 dark:text-amber-400'},
                {key: 'total', label: 'Total', value: campaign.recipients?.length || 0},
            ]}/>

            <div className="divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                <DetailRow label="Statut" value={<StatusBadge status={campaign.status}/>}/>
                <DetailRow label="Canal" value={<ChannelBadge channel={campaign.channel}/>}/>
                {campaign.scheduledAt && <DetailRow label="Programmé" value={new Date(campaign.scheduledAt).toLocaleString('fr-FR')}/>}
            </div>

            {/* Message */}
            <div className={sectionClass}>
                <p className={`${captionClass} mb-2`}>Message</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{campaign.message}</p>
            </div>

            {/* Recipients */}
            <div className="px-4 sm:px-6 pt-4 pb-2">
                <p className={captionClass}>Destinataires ({campaign.recipients?.length || 0})</p>
            </div>
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                {(campaign.recipients || []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                            <Avatar name={r.name || '?'} size="sm"/>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{r.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{r.phone}</p>
                            </div>
                        </div>
                        {r.sent === true && <CheckCircle size={16} className="text-green-500 flex-shrink-0"/>}
                        {r.sent === false && <XCircle size={16} className="text-red-500 flex-shrink-0"/>}
                    </div>
                ))}
            </div>

            {/* Action log */}
            {campaign.log?.length > 0 && (
                <div className="px-4 sm:px-6 py-4">
                    <p className={`${captionClass} mb-2`}>Journal</p>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {campaign.log.map((e, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                                <span className="text-gray-400 font-mono flex-shrink-0">
                                    {new Date(e.time).toLocaleTimeString('fr-FR')}
                                </span>
                                <span className={`font-medium flex-shrink-0 ${e.action.includes('error') ? 'text-red-500' : e.action.includes('complete') ? 'text-green-600' : 'text-gray-500'}`}>
                                    [{e.action}]
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">{e.detail}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ─── Create Campaign Modal ────────────────────────────────────────────────────
// Step 1: Target Selection  |  Step 2: Configuration  |  Step 3: Recap/Confirm

const CreateCampaignModal = ({onClose, onCreated, accountId}) => {
    const [step, setStep] = useState(1);

    // ── Step 1 ──────────────────────────────────────────────────────────────
    const [nameFilter,      setNameFilter]     = useState('');
    const [phoneFilter,     setPhoneFilter]    = useState('');
    const [debouncedName,   setDebouncedName]  = useState('');
    const [selectedClients, setSelectedClients] = useState([]);
    const loadMoreRef       = useRef(null);
    const listContainerRef  = useRef(null);

    // ── Step 2 ──────────────────────────────────────────────────────────────
    const [campaignName, setCampaignName] = useState('');
    const [channel,      setChannel]      = useState('whatsapp');
    const [message,      setMessage]      = useState('');
    const [useSchedule,  setUseSchedule]  = useState(false);
    const [scheduledAt,  setScheduledAt]  = useState('');

    // ── Step 3 ──────────────────────────────────────────────────────────────
    const [confirmed,    setConfirmed]    = useState(false);
    const [isSending,    setIsSending]    = useState(false);
    const [sendProgress, setSendProgress] = useState({current: 0, total: 0, currentName: ''});
    const [showTestSend, setShowTestSend] = useState(false);
    const [actionLog,    setActionLog]    = useState([]);

    // Debounce name → API
    useEffect(() => {
        const t = setTimeout(() => setDebouncedName(nameFilter), 350);
        return () => clearTimeout(t);
    }, [nameFilter]);

    const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = useInfiniteQuery({
        queryKey: ['clients-for-marketing', debouncedName],
        queryFn: fetchClientsPage,
        initialPageParam: 1,
        getNextPageParam: lp => lp.nextPage,
        staleTime: 1000 * 60 * 2,
    });

    const allLoaded      = data?.pages.flatMap(p => p.items) ?? [];
    // Combined filter: API handles name, client-side handles phone
    const visibleClients = phoneFilter.trim()
        ? allLoaded.filter(c =>
            (c.phone || '').includes(phoneFilter.trim()) ||
            (c.name  || '').toLowerCase().includes(phoneFilter.toLowerCase()))
        : allLoaded;

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        const el = loadMoreRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
            {root: listContainerRef.current, threshold: 0.1}
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Computed recipient validation
    const uniqueRecipients  = deduplicateByPhone(selectedClients);
    const validRecipients   = uniqueRecipients.filter(c =>  validatePhone(c.phone));
    const invalidRecipients = uniqueRecipients.filter(c => !validatePhone(c.phone));
    const duplicateCount    = selectedClients.length - uniqueRecipients.length;

    const toggleClient = (c) =>
        setSelectedClients(prev =>
            prev.find(x => x.id === c.id) ? prev.filter(x => x.id !== c.id) : [...prev, c]
        );

    const selectAllVisible = () =>
        setSelectedClients(prev => {
            const ids = new Set(prev.map(c => c.id));
            return [...prev, ...visibleClients.filter(c => !ids.has(c.id))];
        });

    const addLog = (action, detail) =>
        setActionLog(prev => [...prev, {time: new Date().toISOString(), action, detail}]);

    const handleSend = async () => {
        if (!confirmed)              { toast.error('Veuillez cocher la confirmation'); return; }
        if (!validRecipients.length) { toast.error('Aucun destinataire valide');       return; }

        setIsSending(true);
        addLog('send_start', `${validRecipients.length} destinataires valides`);

        const recipients = validRecipients.map(c => ({...c, sent: null}));
        let sentCount = 0, failedCount = 0;

        for (let i = 0; i < recipients.length; i++) {
            const client = recipients[i];
            const phone  = (client.phone || '').replace(/[\s+]/g, '');
            setSendProgress({current: i + 1, total: recipients.length, currentName: client.name});
            try {
                if (channel === 'whatsapp') {
                    await axios.post(`${WHATSAPP_API}/send`, {
                        number: accountId, message, to: phone, type: 'chat',
                    });
                }
                recipients[i].sent = true;
                sentCount++;
            } catch (err) {
                recipients[i].sent = false;
                failedCount++;
                addLog('send_error', `${client.name} (${phone}) : ${err?.response?.data?.message || 'Erreur'}`);
            }
        }

        const finalLog = [...actionLog, {time: new Date().toISOString(), action: 'send_complete', detail: `${sentCount} succès, ${failedCount} échecs`}];
        const campaign = {
            id: Date.now().toString(),
            name: campaignName, channel, message, recipients,
            status: sentCount > 0 ? 'sent' : 'failed',
            stats:  {sent: sentCount, failed: failedCount, invalid: invalidRecipients.length, duplicates: duplicateCount},
            scheduledAt: useSchedule ? scheduledAt : null,
            log: finalLog,
            createdAt: new Date().toISOString(),
        };

        saveCampaigns([campaign, ...loadCampaigns()]);
        setIsSending(false);
        toast.success(`Campagne terminée : ${sentCount} envoyés, ${failedCount} échecs`);
        onCreated(campaign);
        onClose();
    };

    const STEPS = ['Destinataires', 'Configuration', 'Confirmation'];

    const goNext = () => {
        if (step === 1 && !selectedClients.length) { toast.error('Sélectionnez au moins un destinataire'); return; }
        if (step === 2 && !campaignName.trim())     { toast.error('Entrez un nom de campagne'); return; }
        if (step === 2 && !message.trim())          { toast.error('Rédigez un message'); return; }
        setStep(s => s + 1);
    };

    return (
        <>
        <Modal
            title="Nouvelle campagne"
            subtitle={`Étape ${step} / ${STEPS.length} · ${STEPS[step - 1]}`}
            onClose={onClose}
            size="lg"
            bodyClass=""
            footer={
                <ModalFooter
                    cancelLabel={step > 1 ? 'Retour' : 'Annuler'}
                    onCancel={() => step > 1 ? setStep(s => s - 1) : onClose()}
                    onConfirm={step < 3 ? goNext : handleSend}
                    saving={isSending}
                    disabled={step === 3 && (!confirmed || !validRecipients.length)}
                    label={step < 3 ? 'Suivant' : isSending ? 'Envoi en cours…' : `Envoyer (${validRecipients.length})`}
                />
            }
        >
            {/* Step tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
                {STEPS.map((s, i) => (
                    <button key={i} type="button" onClick={() => i + 1 < step && setStep(i + 1)}
                        className={`flex-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                            step === i + 1 ? 'text-primary border-primary bg-primary/5'
                            : step > i + 1 ? 'text-primary-dark dark:text-primary border-transparent cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                           : 'text-gray-400 border-transparent cursor-default'
                        }`}
                    >
                        <span className="inline-flex items-center gap-1.5">
                            {step > i + 1
                                ? <Check size={13}/>
                                : <span className="w-4 h-4 rounded-full border border-current text-[10px] flex items-center justify-center leading-none">{i + 1}</span>
                            }
                            <span className="hidden sm:inline">{s}</span>
                        </span>
                    </button>
                ))}
            </div>

            {/* ── STEP 1: Target Selection ──────────────────────────── */}
            {step === 1 && (
                <div>
                    {/* Filters */}
                    <div className={`${sectionClass} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input type="text" value={nameFilter} onChange={e => setNameFilter(e.target.value)}
                                placeholder="Nom…" aria-label="Filtrer par nom" className={`${fieldClass} pl-9`}/>
                        </div>
                        <div className="relative">
                            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input type="text" value={phoneFilter} onChange={e => setPhoneFilter(e.target.value)}
                                placeholder="Téléphone…" aria-label="Filtrer par téléphone" className={`${fieldClass} pl-9`}/>
                        </div>
                    </div>

                    {/* Selection controls */}
                    <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-semibold text-primary-dark dark:text-primary">{selectedClients.length}</span> sélectionné(s)
                            {visibleClients.length > 0 && <span className="text-gray-400 text-xs"> · {visibleClients.length} visible(s)</span>}
                        </span>
                        <div className="flex items-center gap-1">
                            {(nameFilter || phoneFilter) && (
                                <Button variant="ghost" size="sm" icon={X} onClick={() => { setNameFilter(''); setPhoneFilter(''); }}>Filtres</Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setSelectedClients([])}>Effacer</Button>
                            <Button variant="tinted" size="sm" icon={Check} onClick={selectAllVisible}>Tout sélectionner</Button>
                        </div>
                    </div>

                    {/* Client list with infinite scroll */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader size={22} className="animate-spin text-primary"/>
                        </div>
                    ) : (
                        <div ref={listContainerRef} className="max-h-72 overflow-y-auto">
                            {visibleClients.length === 0 ? (
                                <ListEmpty icon={Users} title="Aucun client trouvé"/>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {visibleClients.map(client => {
                                        const isSelected = !!selectedClients.find(c => c.id === client.id);
                                        const isValid    = validatePhone(client.phone);
                                        return (
                                            <div key={client.id}
                                                onClick={() => client.phone && toggleClient(client)}
                                                className={`flex items-center gap-3 px-4 sm:px-6 py-2.5 cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                } ${!client.phone ? 'opacity-40 cursor-not-allowed' : ''}`}
                                            >
                                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                                                    {isSelected && <Check size={11} className="text-white"/>}
                                                </div>
                                                <Avatar name={client.name || '?'} size="sm"/>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{client.name}</p>
                                                    <p className={`text-xs font-mono truncate ${!client.phone ? 'text-red-500' : !isValid ? 'text-amber-500' : 'text-gray-500 dark:text-gray-400'}`}>
                                                        {client.phone || 'Pas de numéro'}
                                                    </p>
                                                </div>
                                                {client.phone && !isValid && (
                                                    <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" title="Numéro potentiellement invalide"/>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Infinite scroll sentinel */}
                            <div ref={loadMoreRef} className="py-1">
                                {isFetchingNextPage && (
                                    <div className="flex items-center justify-center py-3 gap-2 text-xs text-gray-400">
                                        <Loader size={13} className="animate-spin text-primary"/> Chargement…
                                    </div>
                                )}
                                {!hasNextPage && allLoaded.length > 0 && (
                                    <p className="text-center text-xs text-gray-400 py-2">{allLoaded.length} client(s) chargé(s)</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Selected client chips */}
                    {selectedClients.length > 0 && (
                        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700">
                            <p className={`${captionClass} mb-2`}>Sélectionnés</p>
                            <div className="flex flex-wrap gap-1.5">
                                {selectedClients.slice(0, 10).map(c => (
                                    <span key={c.id} className="inline-flex items-center gap-1 h-6 pl-2.5 pr-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary">
                                        {c.name?.split(' ')[0] || 'Client'}
                                        <button type="button" onClick={() => toggleClient(c)} aria-label={`Retirer ${c.name}`}
                                                className="p-0.5 rounded-full hover:bg-primary/15"><X size={10}/></button>
                                    </span>
                                ))}
                                {selectedClients.length > 10 && (
                                    <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs">
                                        +{selectedClients.length - 10} autres
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── STEP 2: Configuration ─────────────────────────────── */}
            {step === 2 && (
                <div className="px-4 sm:px-6 py-4 space-y-4">
                    {/* Campaign name */}
                    <div>
                        <label htmlFor="campaign-name" className={labelClass}>
                            Nom de la campagne <span className="text-red-500">*</span> :
                        </label>
                        <input id="campaign-name" type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)}
                            placeholder="Ex: Promo Ramadan 2025" className={fieldClass}/>
                    </div>

                    {/* Channel selector */}
                    <div>
                        <p className={labelClass}>Canal d'envoi :</p>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                {id: 'whatsapp', label: 'WhatsApp', sub: 'Messages instantanés', icon: MessageSquare, iconCls: 'text-green-600 dark:text-green-400'},
                                {id: 'sms', label: 'SMS', sub: 'Bientôt disponible', icon: Phone, iconCls: 'text-blue-600 dark:text-blue-400'},
                            ].map(ch => {
                                const Icon = ch.icon;
                                const active = channel === ch.id;
                                return (
                                    <button key={ch.id} type="button" onClick={() => setChannel(ch.id)}
                                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                                            active ? 'border-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600 hover:border-primary/50'}`}
                                    >
                                        <Icon size={18} className={`${ch.iconCls} flex-shrink-0`}/>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{ch.label}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{ch.sub}</p>
                                        </div>
                                        {active && <Check size={14} className="text-primary flex-shrink-0"/>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Message */}
                    <div>
                        <label htmlFor="campaign-message" className={labelClass}>
                            Message <span className="text-red-500">*</span> :
                        </label>
                        <textarea id="campaign-message" value={message} onChange={e => setMessage(e.target.value)} rows={5}
                            placeholder="Écrivez votre message ici…" className={`${fieldClass} resize-none`}/>
                        <div className="flex items-center justify-between mt-1.5">
                            <div className="flex gap-1.5">
                                {['{nom}', '{téléphone}'].map(v => (
                                    <button key={v} type="button" onClick={() => setMessage(m => m + v)}
                                        className="px-2 py-0.5 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary text-xs font-medium rounded-full hover:bg-primary/20 transition-colors"
                                    >{v}</button>
                                ))}
                            </div>
                            <span className={`text-xs tabular-nums ${message.length > 1000 ? 'text-amber-500' : 'text-gray-400'}`}>{message.length} car.</span>
                        </div>
                        {message.length > 1600 && channel === 'sms' && (
                            <p className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                                <AlertTriangle size={12}/> Long message — sera segmenté en plusieurs SMS
                            </p>
                        )}
                    </div>

                    {/* Optional scheduling */}
                    <div className="py-3 border-y border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-3">
                            <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                <Calendar size={14} className="text-gray-400"/>
                                Programmer l'envoi
                                <span className="text-xs font-normal text-gray-400">(optionnel)</span>
                            </span>
                            <button type="button" role="switch" aria-checked={useSchedule} onClick={() => setUseSchedule(p => !p)}
                                className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${useSchedule ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                            >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${useSchedule ? 'translate-x-5' : ''}`}/>
                            </button>
                        </div>
                        {useSchedule && (
                            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                                min={new Date().toISOString().slice(0, 16)} aria-label="Date d'envoi"
                                className={`${fieldClass} mt-3`}/>
                        )}
                    </div>
                </div>
            )}

            {/* ── STEP 3: Recap / Confirmation ──────────────────────── */}
            {step === 3 && (
                <div>
                    <SummaryStrip items={[
                        {key: 'valid', label: 'Valides', value: validRecipients.length, color: 'text-primary-dark dark:text-primary'},
                        {key: 'invalid', label: 'Invalides', value: invalidRecipients.length,
                            color: invalidRecipients.length ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'},
                        {key: 'dupes', label: 'Doublons retirés', value: duplicateCount,
                            color: duplicateCount ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'},
                    ]}/>

                    {/* Invalid phone warning */}
                    {invalidRecipients.length > 0 && (
                        <p className="flex items-start gap-2 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 text-xs text-amber-700 dark:text-amber-400">
                            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
                            <span>
                                <strong>{invalidRecipients.length} numéro(s) invalide(s) ignoré(s) :</strong>{' '}
                                {invalidRecipients.slice(0, 3).map(c => c.name).join(', ')}
                                {invalidRecipients.length > 3 && ` +${invalidRecipients.length - 3} autres`}
                            </span>
                        </p>
                    )}

                    {/* Campaign summary */}
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                        <DetailRow label="Campagne" value={campaignName}/>
                        <DetailRow label="Canal" value={<ChannelBadge channel={channel}/>}/>
                        <DetailRow label="Destinataires" value={`${validRecipients.length} contacts`}/>
                        {useSchedule && scheduledAt && <DetailRow label="Programmé" value={new Date(scheduledAt).toLocaleString('fr-FR')}/>}
                    </div>

                    {/* Message preview */}
                    <div className={sectionClass}>
                        <p className={`${captionClass} mb-2`}>Aperçu du message</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-4">{message}</p>
                        {message.length > 200 && <p className="text-xs text-gray-400 mt-1">({message.length} caractères)</p>}
                    </div>

                    {/* Sample recipients preview */}
                    {validRecipients.length > 0 && (
                        <>
                            <div className="px-4 sm:px-6 pt-4 pb-2">
                                <p className={captionClass}>
                                    Destinataires {validRecipients.length > 5 ? `(5 sur ${validRecipients.length})` : `(${validRecipients.length})`}
                                </p>
                            </div>
                            <div className="divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                                {validRecipients.slice(0, 5).map((c, i) => (
                                    <div key={i} className="flex items-center gap-3 px-4 sm:px-6 py-2.5">
                                        <Avatar name={c.name || '?'} size="sm"/>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.name}</p>
                                            <p className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate">{c.phone}</p>
                                        </div>
                                    </div>
                                ))}
                                {validRecipients.length > 5 && (
                                    <p className="text-xs text-gray-400 text-center py-2">+ {validRecipients.length - 5} autres</p>
                                )}
                            </div>
                        </>
                    )}

                    <div className="px-4 sm:px-6 py-4 space-y-4">
                        {/* Test send trigger */}
                        <Button variant="warning" fullWidth icon={Send} onClick={() => setShowTestSend(true)}>
                            Envoyer un message de test avant d'envoyer
                        </Button>

                        {/* Explicit confirmation checkbox */}
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input type="checkbox" checked={confirmed} onChange={() => setConfirmed(p => !p)}
                                   className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"/>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Je confirme l'envoi à <strong className="text-gray-900 dark:text-white">{validRecipients.length} destinataire(s)</strong>. Cette action est irréversible.
                            </span>
                        </label>

                        {/* Send progress bar */}
                        {isSending && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                        <Loader size={13} className="animate-spin text-primary"/> Envoi en cours…
                                    </span>
                                    <span className="font-semibold tabular-nums text-primary-dark dark:text-primary">{sendProgress.current} / {sendProgress.total}</span>
                                </div>
                                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                                    <div className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                        style={{width: `${sendProgress.total ? (sendProgress.current / sendProgress.total) * 100 : 0}%`}}
                                    />
                                </div>
                                {sendProgress.currentName && (
                                    <p className="text-xs text-gray-400 truncate">Envoi à : {sendProgress.currentName}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

        </Modal>

        {/* Test Send sub-modal, rendered after so it stacks on top */}
        {showTestSend && (
            <TestSendModal message={message} channel={channel} accountId={accountId} onClose={() => setShowTestSend(false)}/>
        )}
        </>
    );
};

// ─── Main Marketing Page ──────────────────────────────────────────────────────

export default function MarketingPage() {
    const [campaigns, setCampaigns] = useState(loadCampaigns());
    const [showCreate, setShowCreate] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [searchQ, setSearchQ] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [accountId, setAccountId] = useState(null);
    const [whatsAppStatus, setWhatsAppStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(false);

    useEffect(() => {
        secureStorage.getItem('selectedAccount').then(acc => {
            if (acc?.id) {
                setAccountId(acc.id);
                checkWhatsAppStatus(acc.id);
            }
        });
    }, []);

    const checkWhatsAppStatus = async (id) => {
        setLoadingStatus(true);
        try {
            const data = await fetchWhatsAppStatus(id);
            setWhatsAppStatus(data);
        } finally {
            setLoadingStatus(false);
        }
    };

    const handleCreated = (_campaign) => {
        setCampaigns(loadCampaigns());
    };

    const handleDelete = (id) => {
        if (!window.confirm('Supprimer cette campagne ?')) return;
        const updated = campaigns.filter(c => c.id !== id);
        saveCampaigns(updated);
        setCampaigns(updated);
        toast.success('Campagne supprimée');
    };

    const filtered = campaigns.filter(c => {
        const matchQ = !searchQ || c.name?.toLowerCase().includes(searchQ.toLowerCase());
        const matchStatus = filterStatus === 'all' || c.status === filterStatus;
        return matchQ && matchStatus;
    });

    const totalSent = campaigns.reduce((s, c) => s + (c.stats?.sent || 0), 0);
    const totalFailed = campaigns.reduce((s, c) => s + (c.stats?.failed || 0), 0);
    const totalRecipients = campaigns.reduce((s, c) => s + (c.recipients?.length || 0), 0);


    // Just check: status true = connected, otherwise check message
    const connected = whatsAppStatus?.status === true ||
        (whatsAppStatus?.deviceId && whatsAppStatus?.message?.toLowerCase().includes('connect'));

    const statusLabels = {sent: 'Envoyé', failed: 'Échoué', sending: 'En cours', draft: 'Brouillon'};
    const chips = [
        searchQ && {key: 'search', label: `Recherche: ${searchQ}`, onRemove: () => setSearchQ('')},
        filterStatus !== 'all' && {key: 'status', label: `Statut: ${statusLabels[filterStatus]}`, onRemove: () => setFilterStatus('all')},
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Header — same layout as the other pages: centred title on desktop */}
            <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                <div className="px-4 py-4 sm:px-8">
                    <div className="page-header-row">
                        <h1 className="page-title">Marketing</h1>
                        <div className="flex items-center gap-1 sm:gap-2">
                            {/* WhatsApp status — click to refresh */}
                            <button
                                type="button"
                                onClick={() => accountId && checkWhatsAppStatus(accountId)}
                                title="Actualiser le statut WhatsApp"
                                className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${
                                    connected
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
                                }`}
                            >
                                {loadingStatus
                                    ? <Loader size={12} className="animate-spin"/>
                                    : <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-400'}`}/>}
                                <span className="hidden sm:inline">WhatsApp</span> {connected ? 'Connecté' : 'Déconnecté'}
                                <RefreshCw size={11} className="opacity-60"/>
                            </button>
                            <HeaderAction icon={Plus} label="Nouvelle campagne" onClick={() => setShowCreate(true)}/>
                        </div>
                    </div>
                </div>
            </header>

            {/* Toolbar: search + status, like the Stock lists */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchQ} onChange={setSearchQ} placeholder="Rechercher une campagne..."/>
                <ToolbarSelect label="Statut" value={filterStatus} onChange={setFilterStatus}>
                    <option value="all">Tous les statuts</option>
                    <option value="sent">Envoyé</option>
                    <option value="failed">Échoué</option>
                    <option value="sending">En cours</option>
                    <option value="draft">Brouillon</option>
                </ToolbarSelect>
            </div>
            <ActiveFilters chips={chips} onClearAll={() => { setSearchQ(''); setFilterStatus('all'); }}/>

            {/* Totaux : bande à filets, comme le Stock */}
            <SummaryStrip items={[
                {key: 'campaigns', label: 'Total campagnes', value: campaigns.length},
                {key: 'recipients', label: 'Destinataires', value: totalRecipients},
                {key: 'sent', label: 'Messages envoyés', value: totalSent, color: 'text-green-600 dark:text-green-400'},
                {key: 'failed', label: 'Échecs', value: totalFailed, color: 'text-red-600 dark:text-red-400'},
            ]}/>

            {/* Campaigns */}
            <ListCard>
                {filtered.length === 0 ? (
                    <ListEmpty
                        icon={Megaphone}
                        title="Aucune campagne trouvée"
                        text={campaigns.length === 0 ? 'Créez votre première campagne' : 'Modifiez vos filtres'}
                        action={campaigns.length === 0 && (
                            <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={() => setShowCreate(true)}>
                                Créer une campagne
                            </Button>
                        )}
                    />
                ) : (
                    <>
                        {/* Mobile list */}
                        <div className={mobileListClass.replace('lg:hidden', 'sm:hidden')}>
                            {filtered.map(campaign => (
                                <div key={campaign.id} className={`${mobileRowClass} cursor-pointer space-y-3`}
                                     onClick={() => setSelectedCampaign(campaign)}>
                                    <div className="flex items-start gap-3">
                                        <Avatar name={campaign.name || '?'} size="sm"/>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{campaign.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{campaign.message}</p>
                                        </div>
                                        <div onClick={e => e.stopPropagation()}>
                                            <CampaignActions campaign={campaign} onView={setSelectedCampaign} onDelete={handleDelete}/>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                        <StatusBadge status={campaign.status}/>
                                        <ChannelBadge channel={campaign.channel}/>
                                        <span className="flex items-center gap-1"><Users size={12}/>{campaign.recipients?.length || 0}</span>
                                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium"><CheckCircle size={12}/> {campaign.stats?.sent || 0}</span>
                                        <span className="flex items-center gap-1 text-red-500 font-medium"><XCircle size={12}/> {campaign.stats?.failed || 0}</span>
                                        <span className="ml-auto">{formatDate(campaign.createdAt)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Campagne</th>
                                    <th className={thClass}>Canal</th>
                                    <th className={`${thClass} text-right`}>Destinataires</th>
                                    <th className={thClass}>Résultats</th>
                                    <th className={thClass}>Statut</th>
                                    <th className={thClass}>Date</th>
                                    <th className={thClass}><span className="sr-only">Actions</span></th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filtered.map(campaign => (
                                    <tr key={campaign.id} onClick={() => setSelectedCampaign(campaign)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <Avatar name={campaign.name || '?'} size="sm"/>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{campaign.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 max-w-[240px]">{campaign.message}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"><ChannelBadge channel={campaign.channel}/></td>
                                        <td className="px-6 py-4 text-right text-sm font-medium tabular-nums text-gray-700 dark:text-gray-300">
                                            {campaign.recipients?.length || 0}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3 text-xs font-medium tabular-nums">
                                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle size={12}/> {campaign.stats?.sent || 0}</span>
                                                <span className="flex items-center gap-1 text-red-500"><XCircle size={12}/> {campaign.stats?.failed || 0}</span>
                                            </div>
                                            {(campaign.recipients?.length || 0) > 0 && (
                                                <div className="mt-1.5 w-24 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                                                    <div className="bg-primary h-1.5 rounded-full"
                                                         style={{width: `${((campaign.stats?.sent || 0) / (campaign.recipients?.length || 1)) * 100}%`}}/>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4"><StatusBadge status={campaign.status}/></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDate(campaign.createdAt)}</td>
                                        <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                            <CampaignActions campaign={campaign} onView={setSelectedCampaign} onDelete={handleDelete}/>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </ListCard>

            {/* Modals */}
            {showCreate && (
                <CreateCampaignModal
                    onClose={() => setShowCreate(false)}
                    onCreated={handleCreated}
                    accountId={accountId}
                />
            )}
            {selectedCampaign && (
                <CampaignDetailsModal
                    campaign={selectedCampaign}
                    onClose={() => setSelectedCampaign(null)}
                />
            )}
        </div>
    );
}
